import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FiChevronDown,
  FiSend,
  FiX,
  FiCheckCircle,
  FiSearch,
  FiLock,
  FiArrowLeft,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import TmisLayout from "../components/TmisLayout";
import "./Performance.css";

const API_BASE = process.env.REACT_APP_API || "http://localhost:3023/api";

const API_ENDPOINTS = {
  getIntakes: `${API_BASE}/getIntakes`,
  getTrainers: `${API_BASE}/getTrainers`,
  getTemplates: `${API_BASE}/getTemplates`,
};

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

const getAuthToken = () =>
  localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

const getAxiosConfig = () => {
  const token = getAuthToken();
  if (!token) return {};
  return { headers: { Authorization: `Bearer ${token}` } };
};

const getLoginUser = () => {
  try {
    const storedUser =
      localStorage.getItem("user") || sessionStorage.getItem("user");
    if (!storedUser) return null;
    return JSON.parse(storedUser);
  } catch (error) {
    console.error("Failed to read login user:", error);
    return null;
  }
};

const formatDate = (dateValue) => {
  if (!dateValue) return "-";
  try {
    return new Date(dateValue).toLocaleDateString("en-GB");
  } catch {
    return "-";
  }
};

const getSortValue = (row, key) => {
  if (key === "soc" || key === "eoc") {
    return row[key] ? new Date(row[key]).getTime() : 0;
  }

  if (key === "auto") {
    return row.auto ? 1 : 0;
  }

  return String(row[key] || "").toLowerCase();
};

const UnauthorizedPage = () => {
  const navigate = useNavigate();

  return (
    <TmisLayout showSidebar={false}>
      <main className="unauthorized-main">
        <div className="unauthorized-card">
          <div className="lock-animation">
            <div className="lock-circle">
              <FiLock />
            </div>
            <span className="pulse-ring"></span>
            <span className="pulse-ring second"></span>
          </div>

          <h1>Access Denied</h1>
          <p>
            You are not authorized to view the Send Assessment page. Please
            contact the administrator if you need access.
          </p>

          <button
            type="button"
            className="unauthorized-btn"
            onClick={() => navigate("/dashboard")}
          >
            <FiArrowLeft />
            Back to Dashboard
          </button>
        </div>
      </main>
    </TmisLayout>
  );
};

const Performance = () => {
  const loginUser = useMemo(() => getLoginUser(), []);
  const role = String(loginUser?.role || "").toLowerCase();

  if (role === "trainer") {
    return <UnauthorizedPage />;
  }

  return <PerformanceContent />;
};

const PerformanceContent = () => {
  const [searchText, setSearchText] = useState("");
  const [selectedIntakeCode, setSelectedIntakeCode] = useState("");
  const [selectedTrainer, setSelectedTrainer] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [sentMessage, setSentMessage] = useState("");

  const [intakes, setIntakes] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [autoMap, setAutoMap] = useState({});

  const [loading, setLoading] = useState(false);
  const [pageMessage, setPageMessage] = useState("");

  const [sortConfig, setSortConfig] = useState({
    key: "intakeCode",
    direction: "asc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const loadData = useCallback(async () => {
    setLoading(true);
    setPageMessage("");

    try {
      const [intakeRes, trainerRes, templateRes] = await Promise.all([
        axios.get(API_ENDPOINTS.getIntakes, getAxiosConfig()),
        axios.get(API_ENDPOINTS.getTrainers, getAxiosConfig()),
        axios.get(API_ENDPOINTS.getTemplates, getAxiosConfig()),
      ]);

      const intakeRows = Array.isArray(intakeRes.data?.data)
        ? intakeRes.data.data
        : [];

      setIntakes(intakeRows);
      setTrainers(
        Array.isArray(trainerRes.data?.data) ? trainerRes.data.data : []
      );
      setTemplates(
        Array.isArray(templateRes.data?.data) ? templateRes.data.data : []
      );

      const initialAuto = {};
      intakeRows.forEach((item) => {
        initialAuto[item.id] = Number(item.auto) === 1 || item.auto === true;
      });
      setAutoMap(initialAuto);
    } catch (error) {
      console.error("Load performance page data error:", error);
      setPageMessage(
        error.response?.data?.message ||
          "Failed to load intake, trainer or template data."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, selectedIntakeCode, selectedTrainer, pageSize]);

  const intakeRows = useMemo(() => {
    return intakes.map((item) => {
      const trainerName =
        item.trainer_name ||
        item.trainer ||
        item.name_of_trainer ||
        item.created_by ||
        "-";

      return {
        id: item.id,
        intakeCode: item.intake_code || "-",
        course:
          item.course ||
          item.course_name ||
          item.programme ||
          item.program ||
          "TMIS Tourist Guide Training",
        soc: item.soc_date || item.soc || item.start_date || null,
        eoc: item.eoc_date || item.eoc || item.end_date || null,
        intakeType: item.intake_type || "-",
        trainer: trainerName,
        nameOfTrainer: trainerName,
        auto: Boolean(autoMap[item.id]),
        raw: item,
      };
    });
  }, [intakes, autoMap]);

  const filteredIntakes = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();

    return intakeRows.filter((item) => {
      const matchKeyword =
        !keyword ||
        item.intakeCode.toLowerCase().includes(keyword) ||
        item.course.toLowerCase().includes(keyword) ||
        item.intakeType.toLowerCase().includes(keyword) ||
        item.trainer.toLowerCase().includes(keyword) ||
        item.nameOfTrainer.toLowerCase().includes(keyword);

      const matchIntake =
        !selectedIntakeCode || item.intakeCode === selectedIntakeCode;

      const matchTrainer =
        !selectedTrainer ||
        item.trainer === selectedTrainer ||
        item.nameOfTrainer === selectedTrainer;

      return matchKeyword && matchIntake && matchTrainer;
    });
  }, [intakeRows, searchText, selectedIntakeCode, selectedTrainer]);

  const sortedIntakes = useMemo(() => {
    const rows = [...filteredIntakes];

    rows.sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return rows;
  }, [filteredIntakes, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedIntakes.length / pageSize));

  const paginatedIntakes = useMemo(() => {
    const safePage = Math.min(currentPage, totalPages);
    const start = (safePage - 1) * pageSize;
    return sortedIntakes.slice(start, start + pageSize);
  }, [sortedIntakes, currentPage, totalPages, pageSize]);

  const paginationStart =
    sortedIntakes.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;

  const paginationEnd = Math.min(currentPage * pageSize, sortedIntakes.length);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        key,
        direction: "asc",
      };
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  const toggleAuto = (id) => {
    setAutoMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const openSendModal = (row) => {
    setSelectedRow(row);
    setSelectedTemplate("");
    setSentMessage("");
    setShowModal(true);
  };

  const closeSendModal = () => {
    setShowModal(false);
    setSelectedRow(null);
    setSelectedTemplate("");
    setSentMessage("");
  };

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handleSendAssessmentForm = () => {
    if (!selectedTemplate) {
      setSentMessage("Please choose an assessment form template first.");
      return;
    }

    console.log("Send Assessment Form", {
      intake_id: selectedRow?.id,
      intake_code: selectedRow?.intakeCode,
      template_id: selectedTemplate,
      course: selectedRow?.course,
      soc: selectedRow?.soc,
      eoc: selectedRow?.eoc,
      intake_type: selectedRow?.intakeType,
      trainer: selectedRow?.trainer,
      auto: selectedRow?.auto,
    });

    setSentMessage("Assessment form sent successfully.");

    setTimeout(() => {
      closeSendModal();
    }, 900);
  };

  return (
    <TmisLayout
      searchText={searchText}
      onSearchChange={setSearchText}
      searchPlaceholder="Search intake, course, type, trainer..."
      showSidebar={false}
    >
      <div className="performance-page-content">
        <section className="performance-card performance-filter-card accessible-card">
          <div className="performance-title-row">
            <div>
              <h1>Send Assessment Form</h1>
              <p>
                Send assessment forms to students based on intake code, trainer,
                and template.
              </p>
            </div>
          </div>

          {pageMessage && (
            <div className="modal-message warning">{pageMessage}</div>
          )}

          <div className="performance-filter-row one-row-filter">
            <label className="performance-select-group">
              <span>Intake Code</span>
              <div className="performance-select-wrapper">
                <select
                  value={selectedIntakeCode}
                  onChange={(e) => setSelectedIntakeCode(e.target.value)}
                  disabled={loading}
                >
                  <option value="">
                    {loading ? "Loading intakes..." : "All Intake"}
                  </option>

                  {intakes.map((item) => (
                    <option key={item.id} value={item.intake_code}>
                      {item.intake_code}
                    </option>
                  ))}
                </select>
                <FiChevronDown />
              </div>
            </label>

            <label className="performance-select-group">
              <span>Trainer</span>
              <div className="performance-select-wrapper">
                <select
                  value={selectedTrainer}
                  onChange={(e) => setSelectedTrainer(e.target.value)}
                  disabled={loading}
                >
                  <option value="">
                    {loading ? "Loading trainers..." : "All Trainers"}
                  </option>

                  {trainers.map((item) => (
                    <option key={item.id} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>
                <FiChevronDown />
              </div>
            </label>

            <button
              type="button"
              className="performance-search-btn"
              onClick={handleSearch}
              disabled={loading}
            >
              <FiSearch /> Search
            </button>
          </div>
        </section>

        <section className="performance-card performance-table-card accessible-card">
          <div className="performance-card-header">
            <div>
              <h2>Assessment Intakes</h2>
              <p>
                {loading
                  ? "Loading intake records..."
                  : `${sortedIntakes.length} intake record(s) found`}
              </p>
            </div>

            <div className="performance-page-size">
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="performance-table-responsive">
            <table className="performance-form-table">
              <thead>
                <tr>
                  <th>
                    <button type="button" onClick={() => handleSort("intakeCode")}>
                      Intake Code {getSortIcon("intakeCode")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => handleSort("course")}>
                      Course {getSortIcon("course")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => handleSort("soc")}>
                      SOC {getSortIcon("soc")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => handleSort("eoc")}>
                      EOC {getSortIcon("eoc")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => handleSort("intakeType")}>
                      Intake Type {getSortIcon("intakeType")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => handleSort("trainer")}>
                      Trainer {getSortIcon("trainer")}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      onClick={() => handleSort("nameOfTrainer")}
                    >
                      Name of Trainer {getSortIcon("nameOfTrainer")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => handleSort("auto")}>
                      Auto {getSortIcon("auto")}
                    </button>
                  </th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {paginatedIntakes.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="performance-empty-message">
                      {loading ? "Loading..." : "No intake records found."}
                    </td>
                  </tr>
                ) : (
                  paginatedIntakes.map((item) => (
                    <tr key={item.id}>
                      <td data-label="Intake Code">
                        <span className="class-code-badge">
                          {item.intakeCode}
                        </span>
                      </td>

                      <td data-label="Course">{item.course}</td>
                      <td data-label="SOC">{formatDate(item.soc)}</td>
                      <td data-label="EOC">{formatDate(item.eoc)}</td>
                      <td data-label="Intake Type">{item.intakeType}</td>
                      <td data-label="Trainer">{item.trainer}</td>
                      <td data-label="Name of Trainer">{item.nameOfTrainer}</td>

                      <td data-label="Auto">
                        <button
                          type="button"
                          className={`auto-toggle ${item.auto ? "on" : "off"}`}
                          onClick={() => toggleAuto(item.id)}
                          aria-label={`Turn auto ${item.auto ? "off" : "on"}`}
                        >
                          <span className="toggle-circle"></span>
                          <b>{item.auto ? "ON" : "OFF"}</b>
                        </button>
                      </td>

                      <td data-label="Action">
                        <button
                          type="button"
                          className="send-performance-btn"
                          onClick={() => openSendModal(item)}
                        >
                          <FiSend /> Send Assessment Form
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="performance-pagination">
            <div>
              Showing {paginationStart} to {paginationEnd} of{" "}
              {sortedIntakes.length} entries
            </div>

            <div className="performance-pagination-actions">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                First
              </button>

              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              >
                Prev
              </button>

              <span>
                Page {currentPage} of {totalPages}
              </span>

              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
              >
                Next
              </button>

              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
              >
                Last
              </button>
            </div>
          </div>
        </section>
      </div>

      {showModal && (
        <div className="performance-modal-backdrop" role="presentation">
          <div
            className="performance-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sendPerformanceTitle"
          >
            <div className="performance-modal-header">
              <div>
                <h2 id="sendPerformanceTitle">Send Assessment Form</h2>
                <p>Choose an assessment form template before sending.</p>
              </div>

              <button
                type="button"
                className="modal-close-btn"
                onClick={closeSendModal}
                aria-label="Close modal"
              >
                <FiX />
              </button>
            </div>

            {selectedRow && (
              <div className="selected-class-box">
                <div>
                  <span>Intake Code</span>
                  <strong>{selectedRow.intakeCode}</strong>
                </div>
                <div>
                  <span>Course</span>
                  <strong>{selectedRow.course}</strong>
                </div>
                <div>
                  <span>SOC</span>
                  <strong>{formatDate(selectedRow.soc)}</strong>
                </div>
                <div>
                  <span>EOC</span>
                  <strong>{formatDate(selectedRow.eoc)}</strong>
                </div>
                <div>
                  <span>Intake Type</span>
                  <strong>{selectedRow.intakeType}</strong>
                </div>
                <div>
                  <span>Trainer</span>
                  <strong>{selectedRow.trainer}</strong>
                </div>
                <div>
                  <span>Name of Trainer</span>
                  <strong>{selectedRow.nameOfTrainer}</strong>
                </div>
                <div>
                  <span>Auto</span>
                  <strong>{selectedRow.auto ? "ON" : "OFF"}</strong>
                </div>
              </div>
            )}

            <label className="modal-select-group">
              <span>Assessment Form Template</span>
              <div className="performance-select-wrapper">
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                >
                  <option value="">Choose template</option>

                  {templates.map((template) => (
                    <option
                      key={template.template_id || template.id}
                      value={template.template_id || template.id}
                    >
                      {template.title || "Untitled Template"}
                    </option>
                  ))}
                </select>
                <FiChevronDown />
              </div>
            </label>

            {sentMessage && (
              <div
                className={`modal-message ${
                  sentMessage.includes("successfully") ? "success" : "warning"
                }`}
              >
                {sentMessage.includes("successfully") && <FiCheckCircle />}
                {sentMessage}
              </div>
            )}

            <div className="performance-modal-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={closeSendModal}
              >
                Cancel
              </button>

              <button
                type="button"
                className="confirm-send-btn"
                onClick={handleSendAssessmentForm}
              >
                <FiSend /> Send
              </button>
            </div>
          </div>
        </div>
      )}
    </TmisLayout>
  );
};

export default Performance;